import React, { useEffect, useMemo, useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Tooltip, Typography } from '@mui/material';
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import type { TouchPoint, TouchPointHoverPreview, Trip } from '../../models/types';
import { buildTripSequenceModel } from '../../presentation/cards/models';
import type { DenseTone } from '../../presentation/cards/types';

interface TouchPointSequenceRailProps {
  trip: Trip;
  tenant?: string;
  onHoverTouchPoint?: (preview: TouchPointHoverPreview | null) => void;
  onFocusTouchPoint?: (touchPoint: TouchPoint) => void;
  onFocusLngLat?: (lngLat: [number, number]) => void;
  isEditSourceTrip?: boolean;
  selectedTouchPointIds?: string[];
  onToggleTouchPoint?: (touchPoint: TouchPoint) => void;
}

interface RailRect {
  top: number;
  right: number;
  bottom: number;
  left: number;
  centerX: number;
  centerY: number;
}

interface RailLayout {
  nodes: Record<string, RailRect>;
  startHub: RailRect | null;
  endHub: RailRect | null;
}

const toneConfig: Record<DenseTone, { base: string; text: string }> = {
  neutral: { base: '#64748b', text: '#334155' },
  info: { base: '#2563eb', text: '#1d4ed8' },
  accent: { base: '#4f46e5', text: '#4338ca' },
  good: { base: '#0f766e', text: '#0f766e' },
  warn: { base: '#b45309', text: '#b45309' },
  danger: { base: '#b42318', text: '#991b1b' },
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function getStopsPerRow(count: number, availableWidth: number): number {
  if (count <= 3) return count;

  const safeWidth = Math.max(availableWidth, 220);
  const targetBoxWidth = safeWidth < 340 ? 44 : safeWidth < 460 ? 48 : safeWidth < 620 ? 52 : 56;
  const gap = 12;
  const estimated = Math.floor((safeWidth + gap) / (targetBoxWidth + gap));

  return clamp(estimated, 2, Math.min(count, 6));
}

function chunkEntries<T>(items: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) return [items];
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}

function toRailRect(node: HTMLElement, container: HTMLElement): RailRect {
  const nodeRect = node.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const top = nodeRect.top - containerRect.top;
  const left = nodeRect.left - containerRect.left;
  const right = left + nodeRect.width;
  const bottom = top + nodeRect.height;

  return {
    top,
    left,
    right,
    bottom,
    centerX: left + nodeRect.width / 2,
    centerY: top + nodeRect.height / 2,
  };
}

function buildSameRowPath(from: RailRect, to: RailRect): string {
  const movingRight = to.centerX >= from.centerX;
  const startX = movingRight ? from.right : from.left;
  const endX = movingRight ? to.left : to.right;
  return `M ${startX} ${from.centerY} L ${endX} ${to.centerY}`;
}

function buildRowBreakPath(from: RailRect, to: RailRect): string {
  const startX = from.centerX;
  const startY = from.bottom;
  const endX = to.centerX;
  const endY = to.top;
  const midY = startY + Math.max(10, (endY - startY) / 2);

  return `M ${startX} ${startY} L ${startX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`;
}

function buildHubPath(from: RailRect, to: RailRect): string {
  const startX = from.centerX;
  const startY = from.bottom;
  const endX = to.centerX;
  const endY = to.top;
  const midY = startY + Math.max(8, (endY - startY) / 2);

  return `M ${startX} ${startY} L ${startX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`;
}

function getAnchorClientPosition(target: EventTarget | null): { x: number; y: number } | null {
  if (!(target instanceof Element)) {
    return null;
  }
  const rect = target.getBoundingClientRect();
  return {
    x: rect.left + (rect.width / 2),
    y: rect.top + (rect.height / 2),
  };
}

const TouchPointSequenceRail: React.FC<TouchPointSequenceRailProps> = ({
  trip,
  tenant,
  onHoverTouchPoint,
  onFocusTouchPoint,
  onFocusLngLat,
  isEditSourceTrip = false,
  selectedTouchPointIds = [],
  onToggleTouchPoint,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const startHubRef = useRef<HTMLDivElement | null>(null);
  const endHubRef = useRef<HTMLDivElement | null>(null);
  const nodeRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [containerWidth, setContainerWidth] = useState(0);
  const [layout, setLayout] = useState<RailLayout>({
    nodes: {},
    startHub: null,
    endHub: null,
  });

  const model = useMemo(() => buildTripSequenceModel(trip, tenant), [tenant, trip]);
  const touchPoints = useMemo(
    () => trip.tpDetails.slice().sort((left, right) => left.seqNo - right.seqNo),
    [trip.tpDetails]
  );

  const sequenceEntries = useMemo(() => {
    return model.nodes.map((node, index) => ({
      nodeKey: node.key,
      primary: node.primary,
      secondary: node.secondary,
      widthWeight: node.widthWeight,
      tone: node.tone ?? 'neutral',
      hasBreach: node.hasBreach,
      tooltip: node.tooltip,
      touchPoint: touchPoints[index],
      rowIndex: 0,
    }));
  }, [model.nodes, touchPoints]);

  const rows = useMemo(() => {
    const chunkSize = getStopsPerRow(sequenceEntries.length, containerWidth);
    return chunkEntries(sequenceEntries, chunkSize).map((row, rowIndex) => {
      const logicalEntries = row.map((entry) => ({ ...entry, rowIndex }));
      const visualEntries = rowIndex % 2 === 0 ? logicalEntries : logicalEntries.slice().reverse();
      return { logicalEntries, visualEntries, rowIndex };
    });
  }, [containerWidth, sequenceEntries]);

  const orderedEntries = useMemo(
    () => rows.flatMap((row) => row.logicalEntries),
    [rows]
  );
  const selectedTouchPointIdSet = useMemo(
    () => new Set(selectedTouchPointIds),
    [selectedTouchPointIds]
  );

  const rowByKey = useMemo(() => {
    return orderedEntries.reduce<Record<string, number>>((accumulator, entry) => {
      accumulator[entry.nodeKey] = entry.rowIndex;
      return accumulator;
    }, {});
  }, [orderedEntries]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let frameId = 0;
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measureLayout);
    });

    const measureLayout = () => {
      const nextContainer = containerRef.current;
      if (!nextContainer) return;
      setContainerWidth(nextContainer.clientWidth);

      const nextNodes: Record<string, RailRect> = {};
      orderedEntries.forEach((entry) => {
        const element = nodeRefs.current[entry.nodeKey];
        if (element) {
          nextNodes[entry.nodeKey] = toRailRect(element, nextContainer);
        }
      });

      setLayout({
        nodes: nextNodes,
        startHub: startHubRef.current ? toRailRect(startHubRef.current, nextContainer) : null,
        endHub: endHubRef.current ? toRailRect(endHubRef.current, nextContainer) : null,
      });
    };

    measureLayout();
    resizeObserver.observe(container);

    orderedEntries.forEach((entry) => {
      const element = nodeRefs.current[entry.nodeKey];
      if (element) {
        resizeObserver.observe(element);
      }
    });

    if (startHubRef.current) {
      resizeObserver.observe(startHubRef.current);
    }
    if (endHubRef.current) {
      resizeObserver.observe(endHubRef.current);
    }

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, [orderedEntries, rows.length]);

  const connectorPaths = useMemo(() => {
    const paths: string[] = [];

    for (let index = 0; index < orderedEntries.length - 1; index += 1) {
      const current = orderedEntries[index];
      const next = orderedEntries[index + 1];
      const currentRect = layout.nodes[current.nodeKey];
      const nextRect = layout.nodes[next.nodeKey];

      if (!currentRect || !nextRect) continue;

      if (rowByKey[current.nodeKey] === rowByKey[next.nodeKey]) {
        paths.push(buildSameRowPath(currentRect, nextRect));
      } else {
        paths.push(buildRowBreakPath(currentRect, nextRect));
      }
    }

    const firstNode = orderedEntries[0];
    const lastNode = orderedEntries[orderedEntries.length - 1];
    if (firstNode && layout.startHub && layout.nodes[firstNode.nodeKey]) {
      paths.unshift(buildHubPath(layout.startHub, layout.nodes[firstNode.nodeKey]));
    }
    if (lastNode && layout.endHub && layout.nodes[lastNode.nodeKey]) {
      paths.push(buildHubPath(layout.nodes[lastNode.nodeKey], layout.endHub));
    }

    return paths;
  }, [layout.endHub, layout.nodes, layout.startHub, orderedEntries, rowByKey]);

  const strokeColor = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.34 : 0.3);
  const lastRowIndex = rows.length - 1;
  const lastRowEndsOnRight = lastRowIndex % 2 === 0;

  if (!orderedEntries.length) {
    return null;
  }

  return (
    <Box ref={containerRef} sx={{ position: 'relative', px: 0.1, py: 0.2 }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        <svg width="100%" height="100%" preserveAspectRatio="none">
          {connectorPaths.map((path, index) => (
            <path
              key={`${path}-${index}`}
              d={path}
              fill="none"
              stroke={strokeColor}
              strokeWidth={1.2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, display: 'grid', gap: 0.8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 0.1 }}>
          <Tooltip title={model.startLabel ? `Departure ${model.startLabel}` : 'Origin hub'}>
            <Box
              ref={startHubRef}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '999px',
                border: '1px solid',
                borderColor: alpha(theme.palette.text.primary, 0.18),
                bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.04 : 0.9),
                boxShadow: `0 10px 18px ${alpha(theme.palette.common.black, 0.06)}`,
                color: theme.palette.text.secondary,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WarehouseIcon sx={{ fontSize: 13 }} />
            </Box>
          </Tooltip>
        </Box>

        {rows.map((row) => (
          <Box
            key={`sequence-row-${row.rowIndex}`}
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 0.55,
            }}
          >
            {row.visualEntries.map((entry) => {
              const tone = toneConfig[entry.tone ?? 'neutral'];
              const isSelectedForEdit = isEditSourceTrip && selectedTouchPointIdSet.has(entry.touchPoint.tpId);
              const button = (
                <Box
                  ref={(element: HTMLButtonElement | null) => {
                    nodeRefs.current[entry.nodeKey] = element;
                  }}
                  component="button"
                  type="button"
                  onMouseEnter={(event) =>
                    onHoverTouchPoint?.({
                      touchPoint: entry.touchPoint,
                      anchorClientPosition: {
                        x: event.clientX,
                        y: event.clientY,
                      },
                      source: 'sequence-rail',
                    })
                  }
                  onMouseLeave={() => onHoverTouchPoint?.(null)}
                  onFocus={(event) => {
                    const anchorClientPosition = getAnchorClientPosition(event.currentTarget);
                    if (!anchorClientPosition) {
                      return;
                    }
                    onHoverTouchPoint?.({
                      touchPoint: entry.touchPoint,
                      anchorClientPosition,
                      source: 'sequence-rail',
                    });
                  }}
                  onBlur={() => onHoverTouchPoint?.(null)}
                  onClick={() => {
                    if (isEditSourceTrip) {
                      onToggleTouchPoint?.(entry.touchPoint);
                      return;
                    }
                    onFocusTouchPoint?.(entry.touchPoint);
                    onFocusLngLat?.(entry.touchPoint.lngLat);
                  }}
                  sx={{
                    flexGrow: Math.max(0.85, entry.widthWeight),
                    flexBasis: 0,
                    minWidth: { xs: 40, sm: 46 },
                    minHeight: 34,
                    px: 0.55,
                    py: 0.32,
                    borderRadius: 1.7,
                    border: '1px solid',
                    color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.05,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
                    appearance: 'none',
                    outline: 'none',
                    overflow: 'visible',
                    mt: isSelectedForEdit ? 0.7 : 0,
                    borderColor: isSelectedForEdit
                      ? alpha('#2563eb', 0.72)
                      : isEditSourceTrip
                        ? alpha('#2563eb', entry.hasBreach ? 0.42 : 0.28)
                        : alpha(tone.base, entry.hasBreach ? 0.42 : 0.22),
                    bgcolor: isSelectedForEdit
                      ? alpha('#dbeafe', 0.92)
                      : isEditSourceTrip
                        ? entry.hasBreach
                          ? '#fee2e2'
                          : alpha('#dbeafe', 0.66)
                        : alpha(tone.base, entry.hasBreach ? 0.13 : 0.055),
                    boxShadow: isSelectedForEdit
                      ? `0 10px 20px ${alpha('#2563eb', 0.14)}`
                      : `0 8px 14px ${alpha(theme.palette.common.black, 0.035)}`,
                    '&:hover, &:focus-visible': {
                      transform: 'translateY(-1px)',
                      boxShadow: isEditSourceTrip
                        ? `0 12px 18px ${alpha('#2563eb', 0.12)}`
                        : `0 12px 18px ${alpha(tone.base, 0.11)}`,
                      borderColor: isEditSourceTrip
                        ? alpha('#2563eb', 0.42)
                        : alpha(tone.base, 0.36),
                    },
                  }}
                >
                  {isSelectedForEdit ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translate(-50%, -48%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.35,
                        pointerEvents: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: 11,
                          height: 1.25,
                          borderRadius: 999,
                          bgcolor: alpha('#2563eb', 0.44),
                        }}
                      />
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: '#16a34a',
                          color: '#ffffff',
                          border: '1.5px solid',
                          borderColor: theme.palette.background.paper,
                          boxShadow: `0 4px 10px ${alpha('#16a34a', 0.20)}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 8,
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                      >
                        ✓
                      </Box>
                      <Box
                        sx={{
                          width: 11,
                          height: 1.25,
                          borderRadius: 999,
                          bgcolor: alpha('#2563eb', 0.44),
                        }}
                      />
                    </Box>
                  ) : null}

                  {entry.hasBreach ? (
                    <FiberManualRecordRoundedIcon
                      sx={{
                        position: 'absolute',
                        top: 3,
                        right: 3,
                        fontSize: 7,
                        color: '#dc2626',
                      }}
                    />
                  ) : null}

                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      lineHeight: 1.05,
                      letterSpacing: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.primary}
                    </Typography>
                  {entry.secondary ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: 9.5,
                        lineHeight: 1.05,
                        fontWeight: 400,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.secondary}
                    </Typography>
                  ) : null}
                </Box>
              );

              return <React.Fragment key={entry.nodeKey}>{button}</React.Fragment>;
            })}
          </Box>
        ))}

        <Box sx={{ display: 'flex', justifyContent: lastRowEndsOnRight ? 'flex-end' : 'flex-start', px: 0.1 }}>
          <Tooltip title={model.endLabel ? `Arrival ${model.endLabel}` : 'Return hub'}>
            <Box
              ref={endHubRef}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '999px',
                border: '1px solid',
                borderColor: alpha(theme.palette.text.primary, 0.18),
                bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.04 : 0.9),
                boxShadow: `0 10px 18px ${alpha(theme.palette.common.black, 0.06)}`,
                color: theme.palette.text.secondary,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WarehouseIcon sx={{ fontSize: 13 }} />
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default TouchPointSequenceRail;
